import type { MutatorTx } from "../schema"

// Walk an article's version chain from `baseVersionId` to the current head and
// collect the `appliedSteps` along the way, so a proposal can be rebased onto
// the latest content via `rebaseProposal({ chainSteps })`. Returns:
//
//   - `baseHtml: null` when the proposal has no base version, or that version
//     has been pruned. The caller should supersede the proposal — there's no
//     content to anchor against.
//   - `chainSteps: null` when any version in the chain is missing its step
//     log (legacy/genesis row). The caller falls back to the snapshot rebase
//     path by passing only `baseHtml` + `currentHtml`.
//   - `chainSteps: []` when the proposal is already anchored at head — no
//     intervening edits to rebase against.
//
// Shared by `help-center-proposals.ts` and `help-center-proposal-groups.ts`.
// Lives in this leaf module to avoid a circular import between the two
// mutator files.
export async function loadVersionChain(
	tx: MutatorTx,
	articleId: string,
	baseVersionId: string | null,
): Promise<{
	baseHtml: string | null
	currentHtml: string
	chainSteps: unknown[] | null
}> {
	const versions = await tx.query.helpCenterArticleVersions
		.where("articleId", "=", articleId)
		.where("language", "=", "en")
		.run()

	const sorted = [...versions].sort((a, b) => a.createdAt - b.createdAt)
	const head = sorted[sorted.length - 1]
	const currentHtml =
		head && typeof head.content === "string" ? head.content : ""

	if (!baseVersionId) {
		return { baseHtml: null, currentHtml, chainSteps: null }
	}

	const baseIdx = sorted.findIndex((v) => v.id === baseVersionId)
	if (baseIdx < 0) {
		return { baseHtml: null, currentHtml, chainSteps: null }
	}

	const base = sorted[baseIdx]!
	const baseHtml = typeof base.content === "string" ? base.content : ""

	if (baseIdx === sorted.length - 1) {
		return { baseHtml, currentHtml, chainSteps: [] }
	}

	const chain: unknown[] = []
	for (let i = baseIdx + 1; i < sorted.length; i++) {
		const v = sorted[i]!
		if (!Array.isArray(v.appliedSteps)) {
			return { baseHtml, currentHtml, chainSteps: null }
		}
		chain.push(...(v.appliedSteps as unknown[]))
	}
	return { baseHtml, currentHtml, chainSteps: chain }
}
